import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Building2,
  DollarSign,
  Loader2,
  RefreshCw,
  Home,
  PiggyBank,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const PortfolioValuation = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [portfolio, setPortfolio] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPortfolio = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);
    
    try {
      const response = await axios.get(`${API}/portfolio/valuation`, { headers });
      setPortfolio(response.data);
    } catch (error) {
      toast.error("Failed to load portfolio valuation");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPortfolio();
  }, [token]);

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const PriceChangeBadge = ({ change }) => {
    if (!change && change !== 0) return null;
    const isPositive = change >= 0;
    
    return (
      <Badge className={`${isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} flex items-center gap-1`}>
        {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {isPositive ? '+' : ''}{change.toFixed(1)}%
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="skeleton h-8 w-64" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-32 rounded-xl" />)}
        </div>
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="portfolio-valuation-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Portfolio Valuation</h1>
          <p className="text-slate-500 mt-1">
            Estimated values and market trends for your properties
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchPortfolio(false)}
          disabled={refreshing}
        >
          {refreshing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Refresh Values
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Total Portfolio Value</p>
                <p className="text-3xl font-bold mt-1">
                  {formatCurrency(portfolio?.total_portfolio_value)}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <PiggyBank className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <PriceChangeBadge change={portfolio?.average_price_change_percent} />
              <span className="text-green-100 text-xs">avg. market change</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Monthly Rent Potential</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatCurrency(portfolio?.total_monthly_rent_estimate)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-slate-400 text-xs mt-4">Based on market estimates</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Annual Rent Potential</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatCurrency(portfolio?.annual_rent_estimate)}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="text-slate-400 text-xs mt-4">Projected yearly income</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Properties</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {portfolio?.total_properties || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-amber-600" />
              </div>
            </div>
            <p className="text-slate-400 text-xs mt-4">In your portfolio</p>
          </CardContent>
        </Card>
      </div>

      {/* Property List with Valuations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="w-5 h-5" />
            Property Valuations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {portfolio?.properties?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500">No properties in portfolio</p>
            </div>
          ) : (
            <div className="space-y-4">
              {portfolio?.properties?.map((prop, index) => (
                <div
                  key={prop.property_id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4 mb-3 sm:mb-0">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                      <Home className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{prop.address}</p>
                      <p className="text-sm text-slate-500">
                        Source: {prop.source === "estimated" ? "Market Estimate" : prop.source}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 sm:gap-8">
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-slate-500">Estimated Value</p>
                      <p className="text-lg font-bold text-slate-900">
                        {formatCurrency(prop.estimated_value)}
                      </p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-slate-500">Rent Estimate</p>
                      <p className="text-lg font-semibold text-green-700">
                        {formatCurrency(prop.rent_estimate)}/mo
                      </p>
                    </div>
                    <div>
                      <PriceChangeBadge change={prop.price_change_percent} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <p className="text-xs text-slate-400 text-center mt-6">
        * Property valuations are estimates based on market data and may not reflect actual market value. 
        For accurate valuations, consult a licensed appraiser.
      </p>
    </div>
  );
};

export default PortfolioValuation;
